import React, { useEffect, useState } from 'react';
import { GenericButton } from 'components/GenericButton';
import { GenericCheckbox } from 'components/GenericCheckbox';
import { GenericTextInput } from 'components/GenericTextInput';
import { GenericTooltip } from 'components/GenericTooltip';
import { editProfile } from 'containers/ProfileManager/redux/profile.actions';
import { SET_PROFILE_LAYOUT_TYPE } from 'containers/ProfileManager/redux/profile.types';
import { useProfileForm } from 'containers/ProfileManager/hooks/useProfileForm';
import { useVerifyIsMobile } from 'hooks/useVerifyIsMobile';
import { useSelector, useDispatch } from 'react-redux';
import { getTranslation } from 'utils/getTranslation';
import { ProfileImageOption } from '../ProfileImageOption';
import { ProfilesHeader } from '../ProfilesHeader';
import { KidsTooltipContent } from '../KidsTooltipContent';
import './style.scss';
import { getStatusControlPin } from 'containers/EPG/actions/channelBlocker/blocker.actions';
import { hasPinConfigured } from 'containers/EPG/selectors/pin.selectors';
import { MobileGenericHeader } from 'components/MobileGenericHeader';
import {
  hidePinForm,
  showPinForm,
} from 'containers/PinForm/actions/pinForm.actions';
import CustomAlert from 'components/CustomAlert';
import { useKidProfile } from 'containers/ProfileManager/hooks/useKidProfile';

export const EditProfileView = () => {
  const {
    configProfileForm: { user_image, gamification_id },
    profilesInfo: { members },
  } = useSelector(state => state.profilesManager);
  const dispatch = useDispatch();
  const statusPin = useSelector(hasPinConfigured);
  const { isAdmin } = useSelector(state => state.user);
  const translations = useSelector(state => state.translations);
  const asset = useSelector(state => state.asset);
  const { isMobile } = useVerifyIsMobile(768);
  const {
    handleCancelClick,
    handleChange,
    handleCheckChange,
    profileForm,
    setFormAction,
    showTooltip,
    setShowTooltip,
    nameLength,
  } = useProfileForm({ isEditing: true });
  const {
    currentProfile,
    displayAlert,
    setDisplayAlert,
    flowsCheckToEditToKidProfile,
    willChangeToKidProfile,
  } = useKidProfile(profileForm);

  useEffect(() => {
    !statusPin && dispatch(getStatusControlPin());
  }, []);

  const handleEditProfile = () => {
    showTooltip && setShowTooltip(false);
    if (willChangeToKidProfile) return flowsCheckToEditToKidProfile();
    dispatch(editProfile({ ...profileForm, user_image, gamification_id }));
  };

  return (
    <section className="edit-profile-view profile-config">
      {!isMobile && <ProfilesHeader />}
      {isMobile ? (
        <MobileGenericHeader
          caption={getTranslation(
            'updateProfile_access_title_label',
            translations,
          )}
          onBack={handleCancelClick}
        />
      ) : (
        <h2 className="edit-profile-view__title profile-config__title">
          {getTranslation('updateProfile_access_title_label', translations)}
        </h2>
      )}
      <div className="edit-profile-view__form profile-config__form">
        <ProfileImageOption
          profileImg={user_image}
          onOptionClick={setFormAction}
        />
        <GenericTextInput
          classNames={['edit-profile-view__form__input']}
          caption={getTranslation(
            'addProfile_access_nameProfile_label',
            translations,
          )}
          onChange={handleChange}
          value={profileForm.firstname}
          name="profile-name"
          styles={{ marginBottom: 29 }}
          textInputProps={{
            autoFocus: true,
          }}
        />
        {!currentProfile?.admin && (
          <div className="edit-profile-view__form__check">
            <GenericCheckbox
              checked={profileForm.is_kids}
              id={'is_kids'}
              caption={getTranslation(
                'addProfile_access_checkKids_label',
                translations,
              )}
              onCheckChange={handleCheckChange}
            />
            <GenericTooltip
              onCloseTooltip={() => setShowTooltip(false)}
              showTooltip={showTooltip}
              timeToHide={14}
              classNames={['profiles-kids-tooltip']}
            >
              <KidsTooltipContent />
            </GenericTooltip>
          </div>
        )}
        <div className="edit-profile-view__footer">
          <div className="edit-profile-view__footer__top">
            <GenericButton
              textContent={getTranslation(
                'updateProfile_access_option_button_cancel',
                translations,
              )}
              type="secondary"
              onClick={handleCancelClick}
            />
            <GenericButton
              textContent={getTranslation(
                'updateProfile_access_option_button_update',
                translations,
              )}
              type="primary"
              onClick={handleEditProfile}
              disabled={
                profileForm.firstname.length === 0 ||
                profileForm.firstname.length < nameLength.minLength
              }
            />
          </div>
          {isAdmin && !currentProfile?.admin && (
            <GenericButton
              classNames={['profile-full-width-button']}
              textContent={getTranslation(
                'updateProfile_access_option_button_deleteProfile',
                translations,
              )}
              type="secondary"
              onClick={() =>
                dispatch({
                  type: SET_PROFILE_LAYOUT_TYPE,
                  payload: 'DELETE_PROFILE',
                })
              }
            />
          )}
          {displayAlert && (
            <CustomAlert
              text={getTranslation(
                'addProfile_pinAlert_description_label',
                translations,
              )}
              textButton={getTranslation(
                'addProfile_pinAlert_option_button_ok',
                translations,
              )}
              onClose={() => setDisplayAlert(false)}
              asset={asset.avatarSelector_access_icon_error}
            />
          )}
        </div>
      </div>
    </section>
  );
};
